import { useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Two-pane modal where the manager captures the company representative's
 * signature AND the SST technician's signature. Both are drawn on canvases
 * and stored as PNG data-URLs on the assessment doc.
 */
export default function SignaturesModal({ assessmentId, initial, onClose, onSaved }) {
  const companyRef = useRef(null);
  const techRef = useRef(null);
  const [companySigner, setCompanySigner] = useState(initial?.company_signer || "");
  const [techSigner, setTechSigner] = useState(initial?.technical_signer || "");
  const [busy, setBusy] = useState(false);

  const clear = (which) => {
    const ref = which === "company" ? companyRef : techRef;
    ref.current?.clear();
  };

  const grab = (ref) => {
    if (!ref.current || ref.current.isEmpty()) return null;
    return ref.current.getCanvas().toDataURL("image/png");
  };

  const save = async () => {
    const company_signature_base64 = grab(companyRef);
    const technical_signature_base64 = grab(techRef);
    if (!company_signature_base64 && !technical_signature_base64
        && !initial?.company_signature_base64 && !initial?.technical_signature_base64) {
      toast.error("Assine pelo menos um dos campos antes de salvar.");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/assessments/${assessmentId}/signatures`, {
        company_signature_base64,
        technical_signature_base64,
        company_signer: companySigner.trim(),
        technical_signer: techSigner.trim(),
      });
      toast.success("Assinaturas registradas.");
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="signatures-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white w-full max-w-3xl max-h-[92vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-200 flex justify-between items-start bg-slate-50">
          <div>
            <div className="overline text-emerald-900 mb-1">Assinatura eletrônica</div>
            <h2 className="font-display text-xl font-extrabold tracking-tight">Assinar relatório AEP</h2>
            <p className="text-xs text-slate-600 mt-1">
              As assinaturas aparecem na última página do relatório, junto ao hash de integridade.
            </p>
          </div>
          <button onClick={onClose} className="btn btn-ghost text-sm" data-testid="signatures-close">
            <i className="fa-solid fa-xmark text-lg"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          <SignaturePane
            label="Responsável pela empresa"
            signer={companySigner} setSigner={setCompanySigner}
            canvasRef={companyRef} onClear={() => clear("company")}
            testidPrefix="company"
            existing={initial?.company_signature_base64} />
          <SignaturePane
            label="Responsável técnico SST"
            signer={techSigner} setSigner={setTechSigner}
            canvasRef={techRef} onClear={() => clear("tech")}
            testidPrefix="tech"
            existing={initial?.technical_signature_base64} />
        </div>

        <div className="p-5 border-t border-slate-200 flex justify-between gap-2 bg-slate-50">
          <button onClick={onClose} className="btn btn-secondary" data-testid="signatures-cancel">Cancelar</button>
          <button onClick={save} disabled={busy} className="btn btn-primary" data-testid="signatures-submit">
            {busy ? <i className="fa-solid fa-circle-notch fa-spin"></i> : <i className="fa-solid fa-signature"></i>}
            Salvar assinaturas
          </button>
        </div>
      </div>
    </div>
  );
}

function SignaturePane({ label, signer, setSigner, canvasRef, onClear, testidPrefix, existing }) {
  return (
    <div>
      <div className="flex justify-between items-end mb-2">
        <label className="label">{label}</label>
        <button onClick={onClear} className="text-[10px] uppercase font-bold tracking-wider text-slate-500 hover:text-emerald-900"
                data-testid={`signatures-clear-${testidPrefix}`}>
          <i className="fa-solid fa-eraser mr-1"></i> Limpar
        </button>
      </div>
      <input value={signer} onChange={(e) => setSigner(e.target.value)}
             placeholder="Nome completo · Cargo"
             className="input mb-3"
             data-testid={`signatures-signer-${testidPrefix}`} />
      {existing && !canvasRef.current && (
        <div className="text-xs text-slate-500 mb-2 flex items-center gap-2">
          <i className="fa-solid fa-circle-check text-emerald-700"></i>
          Assinatura anterior salva. Desenhe abaixo para sobrescrever.
        </div>
      )}
      <div className="border border-slate-300 bg-white">
        <SignatureCanvas
          ref={canvasRef}
          penColor="#0f172a"
          canvasProps={{
            className: "w-full h-32 sm:h-40",
            "data-testid": `signatures-canvas-${testidPrefix}`,
          }}
        />
      </div>
      <p className="text-[10px] text-slate-400 mt-1">Use o mouse ou toque para desenhar a assinatura.</p>
    </div>
  );
}
